/**
 * Shared expense analysis component.
 *
 * - Property-related scope: pie slices are units, filters are transaction codes
 * - Non-property scope: pie slices are transaction codes
 */

import { skipToken } from "@reduxjs/toolkit/query";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import ReactECharts from "echarts-for-react";
import { useGetExpenseBreakdownQuery } from "@/services/kpi.service";
import DateRangeSelector from "./_DateRangeSelector";

interface ExpensesPieChartProps {
    dateRange: string;
    customRange?: { start: string; end: string };
    title?: string;
    description?: string;
    scope?: "all" | "property_related" | "non_property";
    storageKey?: string;
    helperNote?: string;
}

const CHART_COLORS = [
    "#5470c6", // Blue
    "#91cc75", // Green
    "#fac858", // Yellow
    "#ee6666", // Red
    "#73c0de", // Light Blue
    "#fc8452", // Orange
    "#9a60b4", // Purple
    "#ea7ccc", // Pink
    "#3ba272", // Dark Green
    "#c4ccd3", // Gray
    "#d4a373", // Brown/Tan
    "#6c8ebf", // Medium Blue
    "#b85450", // Dark Red
    "#82b366", // Olive Green
    "#f0a030", // Amber
    "#8e7cc3", // Lavender
    "#67c7b0", // Teal
    "#ff9999", // Salmon
    "#99ccff", // Sky Blue
    "#ffcc99", // Peach
];

function getExpenseDescription(name: string | undefined, code: string) {
    if (!name) return "-";

    const escapedCode = code.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const withDash = new RegExp(`^${escapedCode}\\s*-\\s*`, "i");
    const withoutDash = new RegExp(`^${escapedCode}\\s+`, "i");

    return name.replace(withDash, "").replace(withoutDash, "").trim() || name;
}

/**
 * Assigns colors to pie slices to maximize visual distinction between adjacent slices.
 * Simply cycles through all colors in order.
 */
function assignDistinctColors(sortedData: Array<{ code: string; amount: number }>, colorPalette: string[]): Map<string, string> {
    const colorMap = new Map<string, string>();

    if (sortedData.length === 0) return colorMap;

    // Simply assign colors in order - the palette is already designed for visual distinction
    for (let i = 0; i < sortedData.length; i++) {
        const item = sortedData[i];
        colorMap.set(item.code, colorPalette[i % colorPalette.length]);
    }

    return colorMap;
}

export default function ExpensesPieChart({
    dateRange,
    customRange,
    title,
    description,
    scope = "all",
    storageKey = "kpi-expense-chart",
    helperNote: _helperNote,
}: ExpensesPieChartProps) {
    const { t } = useTranslation();
    const isPropertyRelated = scope === "property_related";
    const expenseDateRangeOptions = useMemo(() => {
        const currentYear = new Date().getFullYear();

        return [
            { value: "current_year_through_last_completed_month", label: "YTD (until last completed month)" },
            ...Array.from({ length: 5 }, (_, index) => {
                const year = currentYear - 1 - index;
                return { value: `year_${year}`, label: String(year) };
            }),
        ];
    }, []);
    const [panelDateRange, setPanelDateRange] = useState(dateRange);
    const [panelCustomRange, setPanelCustomRange] = useState(
        customRange?.start || customRange?.end ? customRange : { start: "", end: "" }
    );

    const [selectedTypes, setSelectedTypes] = useState<string[]>(["OPEX", "CAPEX"]);
    const [excludedCodes, setExcludedCodes] = useState<string[]>([]);
    const [showDetailsTable, setShowDetailsTable] = useState(false);
    const [selectedUnitId, setSelectedUnitId] = useState("");

    // Note: localStorage persistence removed to prevent confusion between top card (YTD)
    // and expanded view (defaults to last 12 months). Each time the card is expanded,
    // it will use the default settings.

    const commonQueryParams = {
        range: panelDateRange,
        start: panelDateRange === "custom" ? panelCustomRange.start : undefined,
        end: panelDateRange === "custom" ? panelCustomRange.end : undefined,
        types: selectedTypes,
        scope,
    };

    const { data, isLoading } = useGetExpenseBreakdownQuery({
        ...commonQueryParams,
        dimension: isPropertyRelated ? "unit" : "code",
    });

    const availableCodes = data?.available_codes || [];
    const selectedCodes = useMemo(() => {
        if (!availableCodes.length) return [];
        return availableCodes
            .filter((code) => !excludedCodes.includes(code.code))
            .map((code) => code.code);
    }, [availableCodes, excludedCodes]);

    // Always use the same query - just don't filter the initial one
    // Only use filtered query when we actually have codes to filter
    const shouldUseFilteredQuery = availableCodes.length > 0 && excludedCodes.length > 0;

    const { data: filteredData, isLoading: filteredLoading } = useGetExpenseBreakdownQuery(
        shouldUseFilteredQuery
            ? {
                  ...commonQueryParams,
                  dimension: isPropertyRelated ? "unit" : "code",
                  codes: selectedCodes,
              }
            : skipToken
    );

    // Use filteredData when filtering, otherwise use original data
    const activeData = shouldUseFilteredQuery ? filteredData : data;
    const chartUnits = activeData?.units || [];
    const chartCategories = activeData?.categories || [];
    const filteredTotal = activeData?.total_amount || 0;

    useEffect(() => {
        if (!isPropertyRelated) {
            setSelectedUnitId("");
            return;
        }

        if (!chartUnits.some((unit) => unit.id === selectedUnitId)) {
            setSelectedUnitId("");
        }
    }, [chartUnits, isPropertyRelated, selectedUnitId]);

    const { data: detailData, isLoading: detailLoading } = useGetExpenseBreakdownQuery(
        isPropertyRelated && selectedUnitId
            ? {
                  ...commonQueryParams,
                  dimension: "unit",
                  business_unit_id: selectedUnitId,
                  codes: excludedCodes.length > 0 ? selectedCodes : undefined,
              }
            : skipToken
    );

    const selectedUnit = chartUnits.find((unit) => unit.id === selectedUnitId);

    const chartHeight = isPropertyRelated ? "440px" : "420px";

    const sortedCategories = useMemo(
        () => [...chartCategories].sort((a, b) => b.amount - a.amount),
        [chartCategories]
    );

    // Build color map from ALL available codes (not just visible/filtered ones)
    // This ensures colors remain consistent when toggling filters
    // Use sortedCategories which has both code and amount information
    const codeColorMap = useMemo(() => {
        // Use smart color assignment to avoid adjacent similar colors
        // Assign colors based on sorted categories
        return assignDistinctColors(
            sortedCategories,
            CHART_COLORS
        );
    }, [sortedCategories]);

    const chartData = useMemo(() => {
        if (isPropertyRelated) {
            return chartUnits.map((unit, index) => ({
                name: unit.name,
                description: unit.name,
                value: unit.amount,
                id: unit.id,
                percentage: unit.percentage,
                itemStyle: {
                    color: CHART_COLORS[index % CHART_COLORS.length],
                },
            }));
        }

        // For non-property (code-based), use the color from codeColorMap
        return chartCategories.map((cat) => ({
            name: cat.code,
            description: getExpenseDescription(cat.name, cat.code),
            value: cat.amount,
            id: cat.code,
            percentage: cat.percentage,
            itemStyle: {
                color: codeColorMap.get(cat.code) || "#cccccc",
            },
        }));
    }, [chartCategories, chartUnits, isPropertyRelated, codeColorMap]);

    const toggleType = (type: string) => {
        setSelectedTypes((prev) => {
            if (prev.includes(type)) {
                if (prev.length === 1) return prev;
                return prev.filter((t) => t !== type);
            }
            return [...prev, type];
        });
    };

    const toggleCode = (code: string) => {
        setExcludedCodes((prev) => {
            if (prev.includes(code)) {
                return prev.filter((c) => c !== code);
            }
            return [...prev, code];
        });
    };

    const chartOption = {
        tooltip: {
            trigger: "item",
            formatter: (params: any) => {
                const value = Number(params.value || 0).toLocaleString("en-US", {
                    style: "currency",
                    currency: "USD",
                    currencyDisplay: "symbol",
                    maximumFractionDigits: 0,
                });
                const description = params?.data?.description ? `<br/>${params.data.description}` : "";
                return `<strong>${params.name}</strong>${description}<br/>${value} (${params.percent}%)`;
            },
        },
        legend: {
            show: false,
        },
        series: [
            {
                name: title || t("kpi.expenses_breakdown"),
                type: "pie",
                radius: ["42%", "82%"],
                center: ["50%", "50%"],
                avoidLabelOverlap: true,
                itemStyle: {
                    borderRadius: 4,
                    borderColor: "#fff",
                    borderWidth: 2,
                },
                label: {
                    show: false,
                },
                emphasis: {
                    label: {
                        show: true,
                        fontSize: 14,
                        fontWeight: "bold",
                    },
                },
                labelLine: {
                    show: false,
                },
                data: chartData,
            },
        ],
    };

    const chartEvents = isPropertyRelated
        ? {
              click: (params: any) => {
                  if (params?.data?.id) {
                      setSelectedUnitId(params.data.id);
                      setShowDetailsTable(true);
                  }
              },
          }
        : undefined;
    const showNoData = !isLoading && !filteredLoading && chartData.length === 0;

    return (
        <div className="rounded-sm border border-stroke bg-white px-5 pt-5 pb-5 shadow-default dark:border-strokedark dark:bg-boxdark">
            <div className="mb-4 flex items-start justify-between">
                <div>
                    <h4 className="flex items-center gap-2 text-base font-semibold">
                        <i className="fa-light fa-chart-pie text-primary" />
                        {title || t("kpi.expenses_breakdown")}
                    </h4>
                    <p className="text-sm text-gray-500">
                        {description || t("kpi.expenses_breakdown_desc")}
                    </p>
                    <div className="mt-3">
                        <DateRangeSelector
                            value={panelDateRange}
                            onChange={setPanelDateRange}
                            customRange={panelCustomRange}
                            onCustomRangeChange={setPanelCustomRange}
                            options={expenseDateRangeOptions}
                        />
                    </div>
                </div>
                <div className="text-right">
                    <p className="text-xl font-bold text-black dark:text-white">
                        {filteredTotal.toLocaleString("en-US", {
                            style: "currency",
                            currency: "USD",
                            currencyDisplay: "symbol",
                            maximumFractionDigits: 0,
                        })}
                    </p>
                    <p className="text-xs text-gray-500">{t("kpi.total_expenses")}</p>
                </div>
            </div>

            <div className="mb-4 flex gap-2">
                <button
                    onClick={() => toggleType("OPEX")}
                    className={`rounded px-3 py-1 text-sm font-medium transition-colors ${
                        selectedTypes.includes("OPEX")
                            ? "bg-primary text-white"
                            : "bg-gray-100 text-gray-600 dark:bg-meta-4 dark:text-gray-300"
                    }`}
                >
                    OPEX
                </button>
                <button
                    onClick={() => toggleType("CAPEX")}
                    className={`rounded px-3 py-1 text-sm font-medium transition-colors ${
                        selectedTypes.includes("CAPEX")
                            ? "bg-primary text-white"
                            : "bg-gray-100 text-gray-600 dark:bg-meta-4 dark:text-gray-300"
                    }`}
                >
                    CAPEX
                </button>
            </div>

            <div className="flex flex-col gap-4 lg:flex-row">
                <div className="flex-1">
                    {(isLoading || filteredLoading) ? (
                        <div className="flex h-80 items-center justify-center">
                            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary"></div>
                        </div>
                    ) : showNoData ? (
                        <div className="flex h-80 items-center justify-center rounded-sm border border-dashed border-stroke dark:border-strokedark">
                            <p className="text-sm text-gray-500">{t("kpi.no_data")}</p>
                        </div>
                    ) : (
                        <>
                            <ReactECharts
                                option={chartOption}
                                style={{ height: chartHeight, width: "100%" }}
                                notMerge={true}
                                onEvents={chartEvents}
                            />
                            {isPropertyRelated && (
                                <p className="mt-2 text-xs text-gray-500">
                                    {selectedUnit
                                        ? t("kpi.selected_unit_transactions", { unit: selectedUnit.name })
                                        : t("kpi.click_unit_slice_hint")}
                                </p>
                            )}
                        </>
                    )}
                </div>

                {availableCodes.length > 0 && (
                    <div
                        className="border-stroke pt-4 lg:flex lg:w-56 lg:flex-col lg:border-l lg:pl-4 lg:pt-0 dark:border-strokedark"
                        style={{ height: chartHeight }}
                    >
                        <p className="mb-2 text-xs font-medium text-gray-500">
                            {t("kpi.filter_by_code")}
                        </p>
                        <div className="space-y-1 overflow-y-auto lg:flex-1 lg:min-h-0">
                            {availableCodes.map((cat, index) => (
                                <label
                                    key={`${cat.code}-${index}`}
                                    className="flex cursor-pointer items-center gap-2 rounded p-1 text-sm hover:bg-gray-50 dark:hover:bg-meta-4"
                                >
                                    <input
                                        type="checkbox"
                                        checked={!excludedCodes.includes(cat.code)}
                                        onChange={() => toggleCode(cat.code)}
                                        className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                                    />
                                    {!isPropertyRelated && (
                                        <span
                                            className="h-3 w-3 shrink-0 rounded-full"
                                            style={{
                                                backgroundColor: codeColorMap.get(cat.code) || "#cccccc",
                                            }}
                                        />
                                    )}
                                    <span className="min-w-0">
                                        <span
                                            className={`block ${
                                                excludedCodes.includes(cat.code)
                                                    ? "text-gray-400 line-through"
                                                    : "text-black dark:text-white"
                                            }`}
                                        >
                                            {cat.code}
                                        </span>
                                        {!isPropertyRelated && cat.name && (
                                            <span className="block truncate text-xs text-gray-500">
                                                {getExpenseDescription(cat.name, cat.code)}
                                            </span>
                                        )}
                                    </span>
                                </label>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {isPropertyRelated && chartData.length > 0 && !showNoData && !isLoading && !filteredLoading && (
                <div className="rounded-sm border border-stroke p-3 dark:border-strokedark">
                    <div className="grid grid-cols-1 gap-2 md:grid-cols-2 xl:grid-cols-4">
                        {chartData.map((item, index) => {
                            const isSelected =
                                isPropertyRelated && selectedUnitId === String(item.id);

                            return (
                                <button
                                    key={String(item.id)}
                                    type="button"
                                    onClick={() => {
                                        if (isPropertyRelated) {
                                            setSelectedUnitId(String(item.id));
                                            setShowDetailsTable(true);
                                        }
                                    }}
                                    className={`flex items-center justify-between rounded-sm border px-3 py-2 text-left transition-colors ${
                                        isSelected
                                            ? "border-primary bg-primary/5"
                                            : "border-stroke hover:bg-gray-50 dark:border-strokedark dark:hover:bg-meta-4"
                                    } ${isPropertyRelated ? "cursor-pointer" : "cursor-default"}`}
                                >
                                    <div className="min-w-0 pr-3">
                                        <div className="flex items-center gap-2">
                                            <span
                                                className="h-3 w-3 rounded-full"
                                                style={{
                                                    backgroundColor:
                                                        CHART_COLORS[index % CHART_COLORS.length],
                                                }}
                                            />
                                            <span className="truncate text-sm font-medium text-black dark:text-white">
                                                {item.name}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="shrink-0 text-right">
                                        <div className="text-sm font-semibold text-black dark:text-white">
                                            {Number(item.value).toLocaleString("en-US", {
                                                style: "currency",
                                                currency: "USD",
                                                currencyDisplay: "symbol",
                                                maximumFractionDigits: 0,
                                            })}
                                        </div>
                                        <div className="text-xs text-gray-500">
                                            {Number(item.percentage || 0).toFixed(1)}%
                                        </div>
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}

            {isPropertyRelated ? (
                <div className="mt-4 border-t border-stroke pt-4 dark:border-strokedark">
                    <button
                        type="button"
                        onClick={() => setShowDetailsTable((current) => !current)}
                        className="flex w-full items-center justify-between rounded-sm border border-stroke px-4 py-3 text-left transition-colors hover:bg-gray-50 dark:border-strokedark dark:hover:bg-meta-4"
                    >
                        <span className="text-sm font-semibold text-black dark:text-white">
                            {t("kpi.unit_expense_transactions")}
                        </span>
                        <span className="flex items-center gap-2 text-sm text-primary">
                            {showDetailsTable
                                ? t("kpi.hide_unit_expense_transactions")
                                : t("kpi.show_unit_expense_transactions")}
                            <i className={`fa-light fa-chevron-${showDetailsTable ? "up" : "down"}`} />
                        </span>
                    </button>

                    {showDetailsTable && (
                        <div className="mt-4 overflow-x-auto">
                            {!selectedUnitId ? (
                                <p className="text-sm text-gray-500">
                                    {t("kpi.select_unit_for_transactions")}
                                </p>
                            ) : detailLoading ? (
                                <div className="flex h-32 items-center justify-center">
                                    <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-primary"></div>
                                </div>
                            ) : (
                                <table className="w-full table-auto">
                                    <thead>
                                        <tr className="bg-gray-100 text-left dark:bg-meta-4">
                                            <th className="px-4 py-4 font-medium text-black dark:text-white">
                                                {t("kpi.expense_table_date")}
                                            </th>
                                            <th className="px-4 py-4 font-medium text-black dark:text-white">
                                                {t("kpi.expense_table_code")}
                                            </th>
                                            <th className="px-4 py-4 font-medium text-black dark:text-white">
                                                {t("kpi.expense_table_description")}
                                            </th>
                                            <th className="px-4 py-4 text-right font-medium text-black dark:text-white">
                                                {t("kpi.expense_table_amount")}
                                            </th>
                                            <th className="px-4 py-4 font-medium text-black dark:text-white">
                                                {t("kpi.expense_table_type")}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {(detailData?.transactions || []).length === 0 ? (
                                            <tr>
                                                <td
                                                    colSpan={5}
                                                    className="px-4 py-6 text-center text-sm text-gray-500"
                                                >
                                                    {t("kpi.no_data")}
                                                </td>
                                            </tr>
                                        ) : (
                                            (detailData?.transactions || []).map((transaction) => (
                                                <tr
                                                    key={transaction.id}
                                                    className="border-b border-stroke dark:border-strokedark"
                                                >
                                                    <td className="px-4 py-4 text-black dark:text-white">
                                                        {new Date(transaction.date).toLocaleDateString("en-GB")}
                                                    </td>
                                                    <td className="px-4 py-4 text-black dark:text-white">
                                                        {transaction.code}
                                                    </td>
                                                    <td className="px-4 py-4 text-black dark:text-white">
                                                        {transaction.description || "-"}
                                                    </td>
                                                    <td className="px-4 py-4 text-right text-black dark:text-white">
                                                        {transaction.amount.toLocaleString("en-US", {
                                                            style: "currency",
                                                            currency: "USD",
                                                            currencyDisplay: "symbol",
                                                            maximumFractionDigits: 0,
                                                        })}
                                                    </td>
                                                    <td className="px-4 py-4 text-black dark:text-white">
                                                        {transaction.type}
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    )}
                </div>
            ) : (
                <div className="mt-4 border-t border-stroke pt-4 dark:border-strokedark">
                    <button
                        type="button"
                        onClick={() => setShowDetailsTable((current) => !current)}
                        className="flex w-full items-center justify-between rounded-sm border border-stroke px-4 py-3 text-left transition-colors hover:bg-gray-50 dark:border-strokedark dark:hover:bg-meta-4"
                    >
                        <span className="text-sm font-semibold text-black dark:text-white">
                            {t("kpi.expense_breakdown_details")}
                        </span>
                        <span className="flex items-center gap-2 text-sm text-primary">
                            {showDetailsTable
                                ? t("kpi.hide_expense_breakdown_details")
                                : t("kpi.show_expense_breakdown_details")}
                            <i className={`fa-light fa-chevron-${showDetailsTable ? "up" : "down"}`} />
                        </span>
                    </button>

                    {showDetailsTable && (
                        <div className="mt-4 overflow-x-auto">
                            <table className="w-full table-auto">
                                <thead>
                                    <tr className="bg-gray-100 text-left dark:bg-meta-4">
                                        <th className="px-4 py-4 font-medium text-black dark:text-white">
                                            {t("kpi.expense_table_date")}
                                        </th>
                                        <th className="px-4 py-4 font-medium text-black dark:text-white">
                                            {t("kpi.expense_table_code")}
                                        </th>
                                        <th className="px-4 py-4 font-medium text-black dark:text-white">
                                            {t("kpi.expense_table_description")}
                                        </th>
                                        <th className="px-4 py-4 text-right font-medium text-black dark:text-white">
                                            {t("kpi.expense_table_amount")}
                                        </th>
                                        <th className="px-4 py-4 font-medium text-black dark:text-white">
                                            {t("kpi.expense_table_type")}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(activeData?.transactions || []).length === 0 ? (
                                        <tr>
                                            <td
                                                colSpan={5}
                                                className="px-4 py-6 text-center text-sm text-gray-500"
                                            >
                                                {t("kpi.no_data")}
                                            </td>
                                        </tr>
                                    ) : (
                                        (activeData?.transactions || []).map((transaction) => (
                                            <tr
                                                key={transaction.id}
                                                className="border-b border-stroke dark:border-strokedark"
                                            >
                                                <td className="px-4 py-4 text-black dark:text-white">
                                                    {new Date(transaction.date).toLocaleDateString("en-GB")}
                                                </td>
                                                <td className="px-4 py-4 text-black dark:text-white">
                                                    {transaction.code}
                                                </td>
                                                <td className="px-4 py-4 text-black dark:text-white">
                                                    {transaction.description || "-"}
                                                </td>
                                                <td className="px-4 py-4 text-right text-black dark:text-white">
                                                    {transaction.amount.toLocaleString("en-US", {
                                                        style: "currency",
                                                        currency: "USD",
                                                        currencyDisplay: "symbol",
                                                        maximumFractionDigits: 0,
                                                    })}
                                                </td>
                                                <td className="px-4 py-4 text-black dark:text-white">
                                                    {transaction.type}
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
